<template>
    <div
    >

        <h3
            class="kgr-headline kgr-headline--h3 kgr-headline--text"
            v-t="'Filter'"
        ></h3>
        <div class="filters">
            <kgr-checkbox-group
                v-if="!loading"
                v-model="selectedStatuses"
                :options="availableStatuses"
                inline
                field-for-label="label"
                @input="filterTable"
            />
        </div>
        <kgr-loading v-if="loading" />
        <div v-else>
            <kgr-table
                :is-data-table="true"
                ref="lettersTable"
                class="kgr-table--block"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <kgr-th
                            v-t="'Kind'"
                            sortable
                        />
                        <kgr-th
                            v-t="'signableLetters.column.title'"
                            sortable
                        />
                        <kgr-th
                            v-t="'Erstellt am'"
                            sortable
                        />
                        <kgr-th
                            v-t="'Aktualisiert am'"
                            sortable
                        />
                        <kgr-th
                            v-t="'Digitale Signaturen'"
                        />
                        <kgr-th v-t="'Weitere Aktionen'" />
                    </tr>
                </thead>
                <tbody v-if="letters.length > 0">
                    <tr
                        v-for="letter in letters"
                        :key="letter.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            <a
                                class="kgr-link"
                                :href="`/backend/child/${letter.child.id}/letters`"
                            >
                                {{ letter.child.name }}
                            </a>
                        </td>
                        <td class="kgr-table__cell">
                            {{ letter.title }}
                        </td>
                        <td
                            class="kgr-table__cell"
                            :data-order="letter.created.getTime()"
                        >
                            {{ letter.created.toLocaleString() }}
                        </td>
                        <td
                            class="kgr-table__cell"
                            :data-order="letter.created.getTime()"
                        >
                            {{ letter.updated.toLocaleString() }}
                        </td>
                        <td class="kgr-table__cell" :data-order="getStatus(letter)">
                            <kgr-letter-signatures
                                :letter="letter"
                                style="display: inline-block"
                                @saved="reloadLetter"
                            />
                            <kgr-letter-signatures-status
                                view="employee"
                                :signatures="letter.signatures.filter(c => c.isSignableByEmployee())"
                            />
                            <kgr-letter-signatures-status
                                view="custody"
                                :signatures="letter.signatures.filter(c => c.isSignableByCustody())"
                                :num-available-signees="letter.numCustodians"
                            />
                        </td>
                        <td
                            class="kgr-table__cell kgr-table__cell--actions"
                            style="width: auto"
                        >

                            <a
                                :href="`/backend/child/${letter.child.id}/letters/${letter.id}`"
                                :title="$t('file.browser.download')"
                                target="_blank"
                            >
                                <kgr-icon-download />
                            </a>

                            <a
                                :href="`/backend/child/${letter.child.id}/letters/${letter.id}/preview`"
                                :title="$t('file.browser.view')"
                                target="_blank"
                            >
                                <kgr-icon-preview />
                            </a>
                            <kgr-file-visibility-toggle
                                :show="letter.sharedWithCustodians"
                                :file-id="letter.id"
                                @change="reloadLetter(letter)"
                            />

                            <kgr-icon
                                icon-class="emergency"
                                :disabled="!letter.sharedWithCustodians || letter.numCustodians === 0"
                                :title="getReminderTooltip(letter)"
                                :green-dot="letter.remindedAt !== null"
                                @click="showReminderConfirmLetter = letter"
                            />

                            <kgr-icon
                                icon-class="list-check"
                                :disabled="letter.signatures.filter(s => !s.signed && !s.declined).length > 0"
                                :title="$t('Als abgeschlossen markieren und aus dieser Liste entfernen. Dies ist möglich, sobald alle Unterschriften geleistet (oder optionale abgelehnt) wurden.')"
                                @click="markComplete(letter)"
                            />
                        </td>
                    </tr>
                </tbody>
            </kgr-table>
            <kgr-generic-modal
                :value="!!showReminderConfirmLetter"
                @close="showReminderConfirmLetter = null"
                :width-in-px="400"
                :headline="$t('Vertragserinnerung senden')"
            >
                <template #default>
                    {{ $t('Möchten Sie die Sorgeberechtigten per E-Mail an die Unterzeichnung des Vertrages erinnern?')  }}
                </template>
                <template #footer>
                    <kgr-button
                        :value="$t('E-Mails senden')"
                        @click="sendReminderNotifications(showReminderConfirmLetter)"
                    />
                </template>
            </kgr-generic-modal>
        </div>
    </div>
</template>

<script>
import CheckboxGroup from '../../common/form/checkbox/CheckboxGroup.vue';
import EventBus from '../../../js/service/EventBus.js';
import FileVisibilityToggle from '../../exchange-directory/FileVisibilityToggle.vue';
import GenericModal from '../../common/modal/GenericModal.vue';
import Icon from '../../common/icon/Icon.vue';
import IconDownload from '../../common/icon/IconDownload.vue';
import IconPreview from '../../common/icon/IconPreview.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import LetterSignatures from './LetterSignatures.vue';
import LetterSignaturesStatus from './LetterSignaturesStatus.vue';
import Loading from '../../common/Loading.vue';
import SignableLetterRepository from '../../../js/repository/SignableLetterRepository.js';

export default {
    components: {
        'kgr-loading': Loading,
        'kgr-letter-signatures-status': LetterSignaturesStatus,
        'kgr-letter-signatures': LetterSignatures,
        'kgr-button': KgrButton,
        'kgr-file-visibility-toggle': FileVisibilityToggle,
        'kgr-icon-preview': IconPreview,
        'kgr-icon-download': IconDownload,
        'kgr-icon': Icon,
        'kgr-generic-modal': GenericModal,
        'kgr-checkbox-group': CheckboxGroup,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            forceTableRefresh: false,
            letters: [],
            showReminderConfirmLetter: null,
            selectedStatuses: [],
            dtInstance: null,
            loading: true
        }
    },
    computed : {
        availableStatuses() {
            return [
                {value: 'complete', label: this.$t('Abgeschlossene Verträge ({count})', {count: this.letters.filter(c => this.getStatus(c) === 'complete').length})},
                {value: 'incomplete', label: this.$t('Unvollständige Verträge ({count})', {count: this.letters.filter(c => this.getStatus(c) === 'incomplete').length})}
            ];
        },
    },
    mounted() {
        this.loadLetters();
    },
    methods: {
        markComplete(letter) {
            this.loading = true;
            SignableLetterRepository.markComplete(letter.id)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.letters = this.letters.filter(c => c.id !== letter.id);
                    const countCompleted = this.letters.filter(c => this.getStatus(c) === 'complete').length;
                    EventBus.$emit('updatedCounterBadge', {name: 'signableLettersBadge', value: countCompleted});

                    const selectedStatusValues = this.selectedStatuses.map(s => s.value);
                    this.selectedStatuses = this.availableStatuses.filter(s => selectedStatusValues.includes(s.value));
                }).catch(() => {
                    this.$notify.error('saveChanges.error');
                }).finally(() => {
                    this.loading = false;
                });
        },
        getStatus(letter) {
            if(letter.signatures.every(s => s.signed || s.declined)) {
                return 'complete';
            }
            return 'incomplete';
        },
        filterTable() {
            this.dtInstance = $(this.$refs.lettersTable.$el).DataTable();
            if (this.dtInstance) {
                const filterFunction = (settings, data, dataIndex, rowData) => {
                    const status = rowData[4]['@data-order'];

                    if (this.selectedStatuses.length === 0) {
                        return true;
                    }

                    return this.selectedStatuses.map(s => s.value).includes(status);
                };

                // Add filter to DataTables
                $.fn.dataTable.ext.search.push(filterFunction);
                this.dtInstance.draw();
            }
        },

        getReminderTooltip(letter) {
            let remindedAtNote = letter.remindedAt !== null ? `. ${this.$t('Zuletzt gesendet am {date}.', { date: letter.remindedAt.toLocaleString() })}` : '';
            if(letter.numCustodians === 0) {
                return this.$t('Sie können keine Erinnerung senden, weil keine Sorgeberechtigten definiert sind') + remindedAtNote;
            }
            if(!letter.sharedWithCustodians) {
                return this.$t('Sie können keine Erinnerung an die Sorgeberechtigten senden, weil der Vertrag nicht freigegeben ist') + remindedAtNote;
            }
            return this.$t('signableLetters.reminder.title') + remindedAtNote;
        },
        reloadLetter(savedLetter) {
            SignableLetterRepository.find(savedLetter.id)
                .then(letter => {
                    const index = this.letters.findIndex(c => c.id === letter.id);
                    if (index !== -1) {
                        this.$set(this.letters, index, letter);
                    }
                    this.$forceUpdate()
                })
                .catch(error => {
                    console.error('There was an error!', error);
                });
        },
        sendReminderNotifications(letter) {
            SignableLetterRepository.sendReminder(letter.id)
                .then(() => {
                    this.$notify.success('saveChanges.success');
                    this.reloadLetter(letter);
                })
                .catch(() => {
                    this.$notify.error('saveChanges.error');
                })
                .finally(() => {
                    this.showReminderConfirmLetter = null;
                });
        },
        loadLetters() {
            this.loading = true;
            SignableLetterRepository.findByKindergarten(this.kindergartenId)
                .then(letters => {
                    this.letters = letters;
                }).catch(error => {
                    console.error('There was an error!', error);
                }).finally(() => {
                    this.loading = false;
                });
        },
    }
}
</script>

<style rel="stylesheet/scss" lang="scss" scoped>

</style>
